/**
 *
 * @module views/components/PromoCardCompact
 * @memberof -Common
 */
import './PromoCardCompact.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Price from '@ulta/core/components/Price/Price';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { hasItems } from '@ulta/core/utils/array/array';
import { constants } from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { isEmptyObject } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import Tag from '@ulta/modules/Tag/Tag';

import interpolateString from '@ulta/utils/interpolateString/interpolateString';

import * as utils from './PromoCardCompact';

/**
 * Represents a PromoCardCompact component
 *
 * @method
 * @param {PromoCardCompactProps} props - React properties passed from composition
 * @returns PromoCardCompact
 */
export const PromoCardCompact = function( props ){
  const {
    title,
    supportingText,
    linkAction,
    tags,
    backgroundColor,
    image,
    listPrice,
    listPriceAccessibilityLabel,
    salePrice,
    salePriceAccessibilityLabel,
    originalPriceLabel,
    kitPrice,
    kitPriceAccessibilityLabel,
    badgeLabel,
    root,
    rootMargin,
    threshold
  } = props;
  const PromoCardCompactRef = useRef( null );

  const hasTags = hasItems( tags );

  if( !image || isEmptyObject( image ) ){
    return null;
  }

  useIntersectionObserver(
    PromoCardCompactRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  const cardContent = () => {
    return (
      <div className='PromoCardCompact__container'>
        <div className='PromoCardCompact__image-content-container'>
          <UltaAsset
            { ...spreadProps( image ) }
          />
          { hasTags && (
            <div className='PromoCardCompact__tags'>
              { tags.map( ( tag, index ) => {
                if( tag?.variant === 'urgency' ){
                  return (
                    <Tag
                      { ...spreadProps( tag ) }
                      key={ `Promo-urgency-tag-${index}` }
                      size='default'
                      timerMeta={ tag.timerMeta ?? utils.DEFAULT_TIMER_META_CONFIG }
                    />
                  );
                }
                if( tag?.message ){
                  return (
                    <Tag
                      { ...spreadProps( tag ) }
                      key={ `Promo-default-tag-${index}` }
                      size='default'
                    />
                  );
                }
                return null;
              } ) }
            </div>
          ) }
        </div>
        <div className='PromoCardCompact__text-content-container'>
          <div className='PromoCardCompact__text-content'>
            { title && (
              <div className='PromoCardCompact__headline'>
                <Text textStyle='title-sm'>
                  { title }
                </Text>
              </div>
            ) }
            { supportingText && (
              <div className='PromoCardCompact__subheadline'>
                <Text
                  htmlTag='p'
                  textStyle='bodyDefault'
                >
                  { supportingText }
                </Text>
              </div>
            ) }
          </div>
          { listPrice && (
            <Price
              listPrice={ listPrice }
              listPriceAccessibilityLabel={ interpolateString( listPriceAccessibilityLabel, [listPrice] ) }
              salePrice={ salePrice }
              salePriceAccessibilityLabel={ interpolateString( salePriceAccessibilityLabel, [salePrice] ) }
              originalPriceLabel={ originalPriceLabel }
              kitPrice={ kitPrice }
              kitPriceAccessibilityLabel={ interpolateString( kitPriceAccessibilityLabel, [kitPrice] ) }
              priceLabel={ badgeLabel }
              variant='large'
            />
          ) }
        </div>
      </div>
    );
  };

  return (
    <div
      ref={ PromoCardCompactRef }
      className={ classNames( 'PromoCardCompact', {
        [`PromoCardCompact--${backgroundColor}`]: backgroundColor
      } ) }
      role='group'
      aria-label='promotion'
    >
      { linkAction ? (
        <Link_Huge
          action={ linkAction }
          ariaLabel={ linkAction?.label }
        >
          { cardContent() }
        </Link_Huge>
      ) : (
        cardContent()
      ) }
    </div>
  );
};


// TODO: Add null checks for timerMeta to fix issue in Palette Utility (PAL-1785): https://ulta.atlassian.net/browse/PAL-1785
export const DEFAULT_TIMER_META_CONFIG = {
  comingSoonLabel: '',
  extendedLabel: '',
  hoursLabel: '',
  todayOnlyLabel: '',
  todayAndTomorrowLabel: '',
  endsTomorrowLabel: '',
  endsTodayLabel: '',
  endsLabel: '',
  endsInLabel: '',
  offerEndedLabel: ''
};

/**
 * Property type definitions
 * @typedef PromoCardCompactProps
 * @type {object}
 * @property {object} title - Sets title
 * @property {object} image - Sets image props
 * @property {string} supportingText - Sets the subheadline / supporting text
 * @property {object} tags - Sets the tags
 * @property {object} linkAction - Sets the linkAction
 * @property {string} listPrice - Sets the listPrice
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label for screen readers
 * @property {string} salePrice - Sets the salePrice
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label for screen readers
 * @property {string} backgroundColor - Sets the background color
 * @property {string} originalPriceLabel - Sets the origginal price label
 * @property {string} kitPrice - Sets the kit price
 * @property {string} kitPriceAccessibilityLabel - Sets the kit price accessibility label for screen readers
 * @property {string} badgeLabel - Sets the badge label
 */
export const propTypes = {
  title: PropTypes.string,
  image: PropTypes.shape( {
    imageUrl: PropTypes.string.isRequired,
    altText: PropTypes.string,
    action: PropTypes.object
  } ),
  supportingText: PropTypes.string,
  tags: PropTypes.arrayOf( PropTypes.shape( {
    message: PropTypes.string,
    variant: PropTypes.string,
    icon: PropTypes.string
  } ) ),
  linkAction: PropTypes.object,
  listPrice: PropTypes.string,
  salePrice: PropTypes.string,
  backgroundColor: PropTypes.string,
  originalPriceLabel: PropTypes.string,
  kitPrice: PropTypes.string,
  badgeLabel: PropTypes.string,
  listPriceAccessibilityLabel: PropTypes.string,
  salePriceAccessibilityLabel: PropTypes.string,
  kitPriceAccessibilityLabel: PropTypes.string
};

/**
 * Default values for passed properties
 * @type object
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

PromoCardCompact.defaultProps = defaultProps;
PromoCardCompact.propTypes = propTypes;

export default PromoCardCompact;
